import { createSlice } from "@reduxjs/toolkit"
import homeAPI from "./homeAPI"
import { createAppAsyncThunk } from "app/hooks"
import { IGetMealsPayload, IHomeState } from "./homeSlice.types"
import { resetStoreAction } from "config"
import { notifyDailyMealsChangedToApp } from "utils/nativeAppHelper"
import { LogMealType } from "model/food/types/base.types"
import { MealType } from "model/food/types"

const getMealsByLogType = (meals: MealType[], logMealType: LogMealType) => {
  return meals.filter((meal) => meal.type === logMealType)
}

let getMealAbortController: AbortController | null = null

export const getMeals = createAppAsyncThunk(
  "home/getMeals",
  async (payload: IGetMealsPayload, { rejectWithValue }) => {
    if (getMealAbortController) {
      getMealAbortController.abort()
    }

    const localController = new AbortController()
    getMealAbortController = localController

    try {
      const response = await homeAPI.getMeals(payload, localController.signal)
      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getMealAbortController === localController) {
        getMealAbortController = null
      }
    }
  },
)

let getDailyProgressAbortController: AbortController | null = null

export const getDailyProgress = createAppAsyncThunk(
  "home/getDailyProgress",
  async (date: string, { rejectWithValue }) => {
    if (getDailyProgressAbortController) {
      getDailyProgressAbortController.abort()
    }

    const localController = new AbortController()
    getDailyProgressAbortController = localController

    try {
      const response = await homeAPI.getDailyProgress(date, localController.signal)
      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getDailyProgressAbortController === localController) {
        getDailyProgressAbortController = null
      }
    }
  },
)

let getMonthlyProgressAbortController: AbortController | null = null

export const getMonthlyProgress = createAppAsyncThunk(
  "home/getMonthlyProgress",
  async ({ from, to }: { from: string; to: string }, { rejectWithValue }) => {
    if (getMonthlyProgressAbortController) {
      getMonthlyProgressAbortController.abort()
    }

    const localController = new AbortController()
    getMonthlyProgressAbortController = localController

    try {
      const response = await homeAPI.getMonthlyProgress(from, to, localController.signal)
      return response
    } catch (err: any) {
      if (localController.signal.aborted) {
        return rejectWithValue({ aborted: true })
      }
      return rejectWithValue(err)
    } finally {
      if (getMonthlyProgressAbortController === localController) {
        getMonthlyProgressAbortController = null
      }
    }
  },
)

export const deleteMeal = createAppAsyncThunk(
  "home/deleteMeal",
  async (mealId: string, { rejectWithValue }) => {
    try {
      await homeAPI.deleteMeal(mealId)
      notifyDailyMealsChangedToApp()
      return mealId
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IHomeState = {
  meals: [],
  getMealsLoading: false,
  getMealsFailed: null,

  dailyProgress: undefined,
  getDailyProgressLoading: false,
  getDailyProgressFailed: undefined,
  getDailyProgressCounter: 0,
  isFirstLoadDiary: true,

  monthlyProgress: undefined,
  getMonthlyProgressLoading: false,
  getMonthlyProgressFailed: undefined,

  selectedDate: undefined,

  isNoMeals: false,
  isNoRecentMeals: false,

  deleteMealLoading: false,
  deleteMealFailed: null,
  deleteMealSuccess: "",
}

export const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    setSelectedDate: (state, { payload }) => {
      state.selectedDate = payload
    },

    resetDailyProgress: (state) => {
      if (state.getDailyProgressLoading) {
        state.dailyProgress = undefined
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getMeals.pending, (state) => {
        state.isNoMeals = false
        state.getMealsLoading = true
        state.meals = []
      })
      .addCase(getMeals.fulfilled, (state, { payload }) => {
        state.getMealsLoading = false
        state.isNoMeals = payload.length === 0

        const breakfast = getMealsByLogType(payload, "BREAKFAST")
        const lunch = getMealsByLogType(payload, "LUNCH")
        const dinner = getMealsByLogType(payload, "DINNER")
        const snack = getMealsByLogType(payload, "SNACK")
        const other = payload.filter((meal) => !meal.type)

        state.isNoRecentMeals = breakfast.length === 0
        state.meals = [
          ...other,
          ...breakfast,
          ...lunch,
          ...dinner,
          ...snack,
        ]
      })
      .addCase(getMeals.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getMealsLoading = false
        state.getMealsFailed = payload
      })

      .addCase(getDailyProgress.pending, (state) => {
        state.getDailyProgressLoading = true
        if (!state.isFirstLoadDiary) {
          state.getDailyProgressCounter += 1
        }
      })
      .addCase(getDailyProgress.fulfilled, (state, { payload }) => {
        state.getDailyProgressLoading = false
        state.dailyProgress = payload
        state.isFirstLoadDiary = false
      })
      .addCase(getDailyProgress.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getDailyProgressLoading = false
        state.getDailyProgressFailed = payload
        state.dailyProgress = undefined
      })

      .addCase(getMonthlyProgress.pending, (state) => {
        state.getMonthlyProgressLoading = true
        state.monthlyProgress = undefined
      })
      .addCase(getMonthlyProgress.fulfilled, (state, { payload }) => {
        state.getMonthlyProgressLoading = false
        state.monthlyProgress = payload
      })
      .addCase(getMonthlyProgress.rejected, (state, { payload }) => {
        if ((payload as any)?.aborted) return
        state.getMonthlyProgressLoading = false
        state.getMonthlyProgressFailed = payload
      })

      .addCase(deleteMeal.pending, (state, { meta: { arg } }) => {
        state.deleteMealLoading = true
        state.meals = state.meals.filter((meal) => meal.id !== arg)
      })
      .addCase(deleteMeal.fulfilled, (state, { payload }) => {
        state.deleteMealLoading = false

        state.deleteMealSuccess = payload

        if (state.meals.length === 0) {
          state.isNoMeals = true
        }
      })
      .addCase(deleteMeal.rejected, (state, { payload }) => {
        state.deleteMealLoading = false
        state.deleteMealFailed = payload
      })

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const { setSelectedDate, resetDailyProgress } = homeSlice.actions

export default homeSlice.reducer
